import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Badge, Table, Form, Alert } from 'react-bootstrap';
import { currentUserService } from '../services/currentUserService';
import { usersApiService } from '../services/apiServices';
import type { UserWithRolesDto } from '../api-client/models/UserWithRolesDto';
import type { RoleName } from '../api-client/models/RoleName';
import { RoleName as RoleNameEnum } from '../api-client/models/RoleName';
import { ApiError } from '../api-client/core/ApiError';

// Маппинг ролей на человеко-читаемые названия
const roleNamesMap: Record<RoleName, string> = {
  [RoleNameEnum.USER]: 'Пользователь',
  [RoleNameEnum.ADMIN]: 'Администратор',
  [RoleNameEnum.ACCOUNTANT]: 'Бухгалтер',
  [RoleNameEnum.HR]: 'HR',
  [RoleNameEnum.RECRUITER]: 'Рекрутер',
  [RoleNameEnum.LAWYER]: 'Юрист',
};

// Маппинг статусов
const statusMap: Record<
  NonNullable<UserWithRolesDto['status']>,
  { label: string; variant: string }
> = {
  active: { label: 'Активен', variant: 'success' },
  pending: { label: 'Ожидает', variant: 'warning' },
  blocked: { label: 'Заблокирован', variant: 'danger' },
};

// Форматирование даты
const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const ProfilePage: React.FC = () => {
  const [user, setUser] = useState<UserWithRolesDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadUser = async () => {
      setIsLoading(true);
      try {
        const userData = await currentUserService.getCurrentUser();
        if (isMounted) {
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to load current user', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadUser();

    const unsubscribe = currentUserService.onUserLoad((userData) => {
      if (isMounted) {
        setUser(userData);
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const userData = await currentUserService.refreshUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== newPasswordConfirm) {
      setPasswordError('Новые пароли не совпадают');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Новый пароль должен содержать минимум 8 символов');
      return;
    }

    setPasswordLoading(true);
    try {
      await usersApiService.changePassword(currentPassword, newPassword, newPasswordConfirm);
      setPasswordSuccess('Пароль успешно изменён');
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const body = err.body as { code?: string; message?: string } | undefined;
        if (body?.code === 'incorrect_password') {
          setPasswordError('Неверный текущий пароль');
        } else if (body?.code === 'no_password_auth') {
          setPasswordError('Для вашего аккаунта не настроена аутентификация по паролю');
        } else {
          setPasswordError(body?.message || 'Ошибка при смене пароля');
        }
      } else {
        setPasswordError('Ошибка при смене пароля');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="mt-5">
        <Card className="text-center">
          <Card.Body>
            <Card.Title>Ошибка загрузки</Card.Title>
            <Card.Text>Не удалось загрузить данные профиля</Card.Text>
            <button className="btn btn-primary" onClick={handleRefresh}>
              Попробовать снова
            </button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const statusInfo = statusMap[user.status] || { label: user.status, variant: 'secondary' };

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>Профиль пользователя</h2>
        </Col>
        <Col xs="auto">
          <button
            className="btn btn-outline-primary"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? <Spinner animation="border" size="sm" /> : 'Обновить'}
          </button>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Основная информация</h5>
            </Card.Header>
            <Card.Body>
              <Table bordered hover responsive="sm">
                <tbody>
                  <tr>
                    <th style={{ width: '40%' }}>Username</th>
                    <td>{user.username}</td>
                  </tr>
                  <tr>
                    <th>ФИО</th>
                    <td>
                      {user.profile?.full_name || <span className="text-muted">Не заполнено</span>}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{user.email}</td>
                  </tr>
                  <tr>
                    <th>Статус</th>
                    <td>
                      <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Верификация email</th>
                    <td>
                      {user.email_verified_at ? (
                        <Badge bg="success">Подтверждён</Badge>
                      ) : (
                        <Badge bg="secondary">Не подтверждён</Badge>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Роли</h5>
            </Card.Header>
            <Card.Body>
              {user.roles && user.roles.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {user.roles.map((role) => (
                    <Badge
                      key={role.name}
                      bg="primary"
                      pill
                      style={{ fontSize: '1rem', padding: '0.5em 1em' }}
                    >
                      {roleNamesMap[role.name] || role.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">Нет назначенных ролей</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Даты</h5>
            </Card.Header>
            <Card.Body>
              <Table bordered hover responsive="sm">
                <tbody>
                  <tr>
                    <th style={{ width: '40%' }}>Дата регистрации</th>
                    <td>{formatDate(user.created_at)}</td>
                  </tr>
                  <tr>
                    <th>Последнее обновление</th>
                    <td>{formatDate(user.updated_at)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Смена пароля</h5>
            </Card.Header>
            <Card.Body>
              {passwordError && (
                <Alert variant="danger" dismissible onClose={() => setPasswordError('')}>
                  {passwordError}
                </Alert>
              )}
              {passwordSuccess && (
                <Alert variant="success" dismissible onClose={() => setPasswordSuccess('')}>
                  {passwordSuccess}
                </Alert>
              )}
              <Form onSubmit={handleChangePassword}>
                <Form.Group className="mb-3">
                  <Form.Label>Текущий пароль</Form.Label>
                  <Form.Control
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    disabled={passwordLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Новый пароль</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={passwordLoading}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Подтверждение нового пароля</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    required
                    minLength={8}
                    disabled={passwordLoading}
                  />
                </Form.Group>
                <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
                  {passwordLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Сохранение...
                    </>
                  ) : (
                    'Изменить пароль'
                  )}
                </button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage;
